import { useCallback, useEffect, useMemo, useState } from "react";
import Cropper from "react-easy-crop";
import { Check, RotateCcw, RotateCw, X } from "lucide-react";

const ASPECTS = [
  { label: "Original", value: null },
  { label: "4 : 3", value: 4 / 3 },
  { label: "3 : 2", value: 3 / 2 },
  { label: "5 : 4", value: 5 / 4 },
  { label: "16 : 9", value: 16 / 9 },
  { label: "1 : 1", value: 1 },
  { label: "4 : 5", value: 4 / 5 },
  { label: "3 : 4", value: 3 / 4 },
  { label: "2 : 3", value: 2 / 3 },
  { label: "9 : 16", value: 9 / 16 },
];

const BORDERS = [
  { label: "No border", value: "none" },
  { label: "Fine double line", value: "fine" },
  { label: "Mithila ornate", value: "ornate" },
];

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

async function produceEditedFile(src, pixels, rotation, border) {
  const image = await loadImage(src);
  const rotRad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));

  const stage = document.createElement("canvas");
  stage.width = Math.round(image.width * cos + image.height * sin);
  stage.height = Math.round(image.width * sin + image.height * cos);
  const sctx = stage.getContext("2d");
  sctx.fillStyle = "#FDFBF7";
  sctx.fillRect(0, 0, stage.width, stage.height);
  sctx.translate(stage.width / 2, stage.height / 2);
  sctx.rotate(rotRad);
  sctx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(pixels.width));
  out.height = Math.max(1, Math.round(pixels.height));
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#FDFBF7";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(stage, pixels.x, pixels.y, pixels.width, pixels.height, 0, 0, out.width, out.height);

  if (border !== "none") {
    const pad = Math.max(12, Math.round(Math.min(out.width, out.height) * 0.022));
    ctx.strokeStyle = "#1A1817";
    ctx.lineWidth = Math.max(3, pad * 0.42);
    ctx.strokeRect(pad, pad, out.width - pad * 2, out.height - pad * 2);
    ctx.strokeStyle = "#B3271E";
    ctx.lineWidth = Math.max(2, pad * 0.22);
    ctx.strokeRect(pad * 2, pad * 2, out.width - pad * 4, out.height - pad * 4);
    if (border === "ornate") {
      ctx.fillStyle = "#B3271E";
      const r = Math.max(4, pad * 0.55);
      const o = pad * 3.4;
      [[o, o], [out.width - o, o], [o, out.height - o], [out.width - o, out.height - o]].forEach(([x, y]) => {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.strokeStyle = "#1A1817";
      ctx.lineWidth = Math.max(1.5, pad * 0.18);
      const step = pad * 2.6;
      for (let x = pad * 2 + step; x < out.width - pad * 2 - step; x += step) {
        ctx.beginPath();
        ctx.arc(x, pad * 2, pad * 0.5, 0, Math.PI);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, out.height - pad * 2, pad * 0.5, Math.PI, 0);
        ctx.stroke();
      }
    }
  }

  return new Promise((resolve) =>
    out.toBlob((blob) => resolve(new File([blob], "artwork.jpg", { type: "image/jpeg" })), "image/jpeg", 0.92)
  );
}

export const ImageEditor = ({ file, src, onUse, onCancel }) => {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : src), [file, src]);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [quarter, setQuarter] = useState(0);
  const [fine, setFine] = useState(0);
  const [aspectIdx, setAspectIdx] = useState(0);
  const [naturalAspect, setNaturalAspect] = useState(4 / 3);
  const [border, setBorder] = useState("none");
  const [pixels, setPixels] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadImage(url).then((img) => setNaturalAspect(img.width / img.height)).catch(() => {});
  }, [url]);

  const onCropComplete = useCallback((_, croppedPixels) => setPixels(croppedPixels), []);
  const aspect = ASPECTS[aspectIdx].value ?? naturalAspect;
  const rotation = quarter * 90 + fine;
  const isPortrait = naturalAspect < 1;

  const apply = async () => {
    if (!pixels) return;
    setExporting(true);
    try {
      const edited = await produceEditedFile(url, pixels, rotation, border);
      onUse(edited);
    } finally {
      setExporting(false);
    }
  };

  const pill = (active) =>
    `rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors ${
      active ? "border-accent bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="mt-6" data-testid="image-editor">
      <div
        className={`relative w-full overflow-hidden border border-border bg-neutral-900 ${
          isPortrait ? "mx-auto h-[460px] max-w-md sm:h-[560px]" : "h-[300px] sm:h-[380px]"
        }`}
      >
        <Cropper
          image={url}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          objectFit="contain"
          restrictPosition={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={(r) => {
            setQuarter(0);
            setFine(r);
          }}
          onCropComplete={onCropComplete}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setQuarter((q) => (q + 3) % 4)}
            data-testid="editor-rotate-left-button"
            aria-label="Rotate 90 degrees left"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-accent hover:text-accent"
          >
            <RotateCcw size={13} />
          </button>
          <button
            type="button"
            onClick={() => setQuarter((q) => (q + 1) % 4)}
            data-testid="editor-rotate-right-button"
            aria-label="Rotate 90 degrees right"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-accent hover:text-accent"
          >
            <RotateCw size={13} />
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {Math.round(rotation)}°
          </span>
        </div>
        <label className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          Straighten
          <input
            type="range"
            min={-45}
            max={45}
            step={0.5}
            value={fine}
            onChange={(e) => { setFine(Number(e.target.value)); }}
            data-testid="editor-straighten-range"
            className="w-32 accent-[#C0392B]"
          />
        </label>
        <label className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          Zoom
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            data-testid="editor-zoom-range"
            className="w-28 accent-[#C0392B]"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="eyebrow mr-1 text-muted-foreground">Crop</span>
        {ASPECTS.map((a, i) => (
          <button key={a.label} type="button" onClick={() => setAspectIdx(i)} data-testid={`editor-aspect-${i}`} className={pill(aspectIdx === i)}>
            {a.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="eyebrow mr-1 text-muted-foreground">Border</span>
        {BORDERS.map((b) => (
          <button key={b.value} type="button" onClick={() => setBorder(b.value)} data-testid={`editor-border-${b.value}`} className={pill(border === b.value)}>
            {b.label}
          </button>
        ))}
      </div>

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={apply}
          disabled={exporting}
          data-testid="editor-apply-button"
          className="flex items-center gap-2 rounded-full bg-foreground px-6 py-2.5 text-[12px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
        >
          <Check size={14} /> {exporting ? "Preparing…" : "Apply edits"}
        </button>
        <button type="button" onClick={onCancel} className="flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-[12px] font-semibold">
          <X size={14} /> Use original photo
        </button>
      </div>
    </div>
  );
};
